import { type Dispatch, type SetStateAction, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Clock, ChevronRight, AlertTriangle, ChevronsDown, Check, MapPin, Navigation } from "lucide-react";
import type { MatchPublic, MatchTeamPublic, EventVenuePublic } from "@dugads/shared";
import { conflictingMatchIds } from "@dugads/shared";
import { ShiftMatchesDialog, type ShiftMode } from "@/components/event-detail/shift-matches-dialog";
import { directionsUrl } from "@/lib/directions";

// 186: mobile replacement for the desktop court x time drag grid. A vertical
// per-court list of scheduled matches (read + reschedule/edit), since the wide
// grid does not fit a phone. The desktop DnD grid stays for md+.
// 193: select mode (checkbox rows + "Move selected" sheet) and a per-row
// "shift from here" action -- the mobile answer to a running-late day, no
// drag required on touch.

interface MobileScheduleProps {
  matches: MatchPublic[];
  matchTeams: Record<string, MatchTeamPublic[]>;
  courtLabel: string;
  canManageMatches: boolean;
  eventId: string;
  venues: EventVenuePublic[]; // 194 -- primary drives the venue title + directions
  setMatches: Dispatch<SetStateAction<MatchPublic[]>>;
  refreshMatches: () => Promise<void>;
}

function fmtTime(iso: string): string {
  return new Date(iso).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
}

export function MobileSchedule({ matches, matchTeams, courtLabel, canManageMatches, eventId, venues, setMatches, refreshMatches }: MobileScheduleProps) {
  // 194 -- primary venue (venues are sorted primary-first) gives the phone
  // schedule the same venue title the desktop grid now shows, single venue
  // included.
  const primaryVenue = venues.find((v) => v.is_primary) ?? venues[0] ?? null;
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [shiftMode, setShiftMode] = useState<ShiftMode | null>(null);

  function toggleSelected(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  // Group by court, each court's matches sorted by start time.
  const byCourt = useMemo(() => {
    const groups = new Map<string, MatchPublic[]>();
    for (const m of matches) {
      const key = m.court_name ?? "Unassigned";
      const arr = groups.get(key) ?? [];
      arr.push(m);
      groups.set(key, arr);
    }
    for (const arr of groups.values()) arr.sort((a, b) => a.starts_at.localeCompare(b.starts_at));
    return [...groups.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [matches]);

  // 191 -- same-field overlaps get the destructive treatment here too, so a
  // coach reviewing on a phone sees the clash the desktop grid frames in red.
  const conflictIds = useMemo(() => conflictingMatchIds(matches), [matches]);

  return (
    <div className="flex flex-col gap-4" data-region="schedule">
      {primaryVenue && (
        <a
          href={directionsUrl(primaryVenue)}
          target="_blank"
          rel="noreferrer"
          className="flex items-center justify-between gap-2 rounded-2xl border border-border bg-card px-3 py-2 text-sm shadow-sm"
          data-region="venue-title"
          data-item-id={primaryVenue.id}
        >
          <span className="inline-flex min-w-0 items-center gap-1.5">
            <MapPin className="h-4 w-4 shrink-0 text-muted-foreground" />
            <span className="truncate font-medium">{primaryVenue.display_name}</span>
          </span>
          <span className="inline-flex shrink-0 items-center gap-1 text-xs text-primary">
            <Navigation className="h-3.5 w-3.5" />
            Directions
          </span>
        </a>
      )}
      {canManageMatches && matches.length > 0 && (
        <div className="flex items-center justify-between px-1" data-region="schedule-toolbar">
          <span className="text-xs text-muted-foreground">
            {selectMode ? `${selectedIds.size} selected` : ""}
          </span>
          <button
            type="button"
            data-action="toggle-select-mode"
            onClick={() => {
              if (selectMode) { setSelectMode(false); setSelectedIds(new Set()); }
              else setSelectMode(true);
            }}
            className={`rounded-md border px-3 py-1.5 text-xs font-semibold transition ${selectMode ? "border-primary bg-primary/10 text-primary" : "border-input"}`}
          >
            {selectMode ? "Done" : "Select"}
          </button>
        </div>
      )}
      {byCourt.length === 0 ? (
        <p className="rounded-2xl border border-border bg-card p-6 text-center text-sm text-muted-foreground shadow-sm">
          No matches scheduled yet.
        </p>
      ) : (
        byCourt.map(([court, courtMatches]) => (
          <div key={court} className="flex flex-col gap-2">
            <p className="px-1 text-xs font-bold uppercase tracking-wide text-muted-foreground">
              {court === "Unassigned" ? `No ${courtLabel.toLowerCase()} assigned` : court}
            </p>
            {courtMatches.map((m) => {
              const teams = matchTeams[m.id] ?? [];
              const home = teams.find((t) => t.role === "home")?.team_name ?? m.name.split(" vs ")[0] ?? "TBD";
              const away = teams.find((t) => t.role === "away")?.team_name ?? m.name.split(" vs ")[1] ?? "TBD";
              const editable = canManageMatches && m._actions.includes("edit");
              const conflicting = conflictIds.has(m.id);
              const selected = selectedIds.has(m.id);
              const inner = (
                <>
                  {selectMode && (
                    <span
                      aria-hidden
                      className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border ${selected ? "border-primary bg-primary text-primary-foreground" : "border-input"}`}
                    >
                      {selected && <Check className="h-3.5 w-3.5" />}
                    </span>
                  )}
                  <div className="flex w-14 shrink-0 flex-col items-start">
                    <span className="inline-flex items-center gap-1 text-sm font-bold">
                      <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                      {fmtTime(m.starts_at)}
                    </span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold">
                      {home} <span className="text-muted-foreground">vs</span> {away}
                    </p>
                    {m.age_group && <p className="truncate text-xs text-muted-foreground">{m.age_group}</p>}
                    {conflicting && (
                      <p className="mt-0.5 inline-flex items-center gap-1 text-xs font-medium text-destructive">
                        <AlertTriangle className="h-3 w-3" aria-hidden />
                        Overlaps another match on this {courtLabel.toLowerCase()}
                      </p>
                    )}
                  </div>
                  {canManageMatches && !selectMode && (
                    <button
                      type="button"
                      title="Running late? Shift this + later matches"
                      data-action="shift-from-here"
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShiftMode({ kind: "from", match: m }); }}
                      className="shrink-0 rounded-lg p-2 text-muted-foreground transition active:scale-95"
                    >
                      <ChevronsDown className="h-5 w-5" />
                    </button>
                  )}
                  {editable && !selectMode && <ChevronRight className="h-5 w-5 shrink-0 text-muted-foreground/50" />}
                </>
              );
              const cls = `flex items-center gap-3 rounded-2xl border bg-card p-3 shadow-sm ${selected ? "border-primary ring-1 ring-primary" : conflicting ? "border-destructive" : "border-border"}`;
              if (selectMode) {
                return (
                  <button
                    key={m.id}
                    type="button"
                    data-match-id={m.id}
                    data-conflict={conflicting ? "true" : undefined}
                    data-selected={selected ? "true" : undefined}
                    onClick={() => toggleSelected(m.id)}
                    className={`${cls} w-full text-left transition-transform active:scale-[.99]`}
                  >
                    {inner}
                  </button>
                );
              }
              return editable ? (
                <Link key={m.id} to={`/matches/${m.id}/edit`} data-match-id={m.id} data-conflict={conflicting ? "true" : undefined} className={`${cls} transition-transform active:scale-[.99]`}>
                  {inner}
                </Link>
              ) : (
                <div key={m.id} data-match-id={m.id} data-conflict={conflicting ? "true" : undefined} className={cls}>
                  {inner}
                </div>
              );
            })}
          </div>
        ))
      )}

      {canManageMatches && selectMode && (
        <div className="sticky bottom-2 flex gap-2" data-region="move-selected-bar">
          <button
            type="button"
            data-action="move-selected"
            disabled={selectedIds.size === 0}
            onClick={() => setShiftMode({ kind: "selection", matchIds: [...selectedIds] })}
            className="flex-1 rounded-2xl bg-primary py-3 text-sm font-semibold text-primary-foreground shadow-sm transition active:scale-[.99] disabled:opacity-40"
          >
            Move selected ({selectedIds.size})
          </button>
          <button
            type="button"
            data-action="clear-selection"
            disabled={selectedIds.size === 0}
            onClick={() => setSelectedIds(new Set())}
            className="rounded-2xl border border-input bg-card px-4 py-3 text-sm font-semibold shadow-sm transition active:scale-[.99] disabled:opacity-40"
          >
            Clear
          </button>
        </div>
      )}

      {canManageMatches && !selectMode && (
        <Link
          to={`/events/${eventId}/matches/new`}
          data-action="add-match"
          className="flex items-center justify-center gap-2 rounded-2xl border border-dashed border-border bg-card py-3 text-sm font-semibold text-primary shadow-sm transition-transform active:scale-[.99]"
        >
          <Plus className="h-4 w-4" />
          Add match
        </Link>
      )}

      {/* 193 -- run-late / move-selected bulk shift */}
      {shiftMode !== null && (
        <ShiftMatchesDialog
          eventId={eventId}
          matches={matches}
          mode={shiftMode}
          courtLabel={courtLabel}
          setMatches={setMatches}
          refreshMatches={refreshMatches}
          onClose={() => setShiftMode(null)}
        />
      )}
    </div>
  );
}
